import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PropTypes from 'prop-types';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import Text from '@zenbusiness/zui/Text';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';

import * as Styled from './SelectionCard.style';

/**
 * Generic selection card component for displaying selectable items
 * Used in SelectionPanel for displaying ideas, names, or other selectable items
 *
 * @param {object} props - Component props
 * @param {object} props.item - Item data object
 * @param {string|number} props.itemId - Unique identifier for the item (used for selection)
 * @param {string} props.displayTitle - Display text for the card
 * @param {boolean} [props.selected=false] - Whether this item is selected
 * @param {Function} props.onClick - Click handler for item selection
 * @param {string} [props.clickstreamElementName] - Clickstream element name for tracking
 * @component
 */
function SelectionCard({
  itemId,
  displayTitle,
  selected = false,
  onClick,
  clickstreamElementName
}) {
  const handleLinkPressed = useClickstreamCallback('linkPressed', {
    ...CLICKSTREAM_COMMON_PROPS,
    elementName: clickstreamElementName || 'selection_card_select',
    elementText: displayTitle
  });

  const handleClick = () => {
    if (onClick) {
      handleLinkPressed();
      onClick(itemId);
    }
  };

  return (
    <Styled.SelectionCardRoot
      aria-pressed={selected}
      role="button"
      selected={selected}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <Text selected={selected} size="sm" weight="medium">
        {displayTitle}
      </Text>
      <KeyboardArrowRightIcon fontSize="small" />
    </Styled.SelectionCardRoot>
  );
}

SelectionCard.propTypes = {
  item: PropTypes.object.isRequired,
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  displayTitle: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  clickstreamElementName: PropTypes.string
};

export default SelectionCard;
